import {
  abs,
  attribute,
  cross,
  dFdx,
  dFdy,
  dot,
  float,
  length,
  max,
  mix,
  mx_noise_float,
  normalize,
  normalView,
  normalViewGeometry,
  positionView,
  positionWorld,
  sign,
  sin,
  smoothstep,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';

type N = any;

/** What an interior's rock looks like, apart from its per-space tint. */
export interface InteriorLook {
  /** The cave's origin relative to the camera (a uniform; refresh every frame). */
  originRel: N;
  /** The two gates, cave-local: the cave takes on each end's colours near it. */
  gateA: [number, number, number];
  gateB: [number, number, number];
  /** Lichen colour near each end. */
  glowA: THREE.Color;
  glowB: THREE.Color;
  /** The direction the rock's layers are stacked along (unit). */
  strata: [number, number, number];
}

/** Thickness of the rock's layers (m). */
const LAYER = 0.62;

/**
 * The normal of a surface with bumps of height `h` (m) on it, from screen-space
 * derivatives (Mikkelsen, "Bump mapping unparametrized surfaces on the GPU"), in view
 * space. Keep it out of branches (derivatives).
 */
export function bumpNormal(h: N): N {
  const dpx = dFdx(positionView);
  const dpy = dFdy(positionView);
  const n = normalView;
  const r1 = cross(dpy, n);
  const r2 = cross(n, dpx);
  const det = dot(dpx, r1);
  const grad = sign(det).mul(dFdx(h).mul(r1).add(dFdy(h).mul(r2)));
  return normalize(abs(det).mul(n).sub(grad));
}

/**
 * The interior's rock at a fragment: layered, lumpy, cracked grey stone with calcite veins,
 * tinted by `look` (rgb tint, wetness) and bumped at several scales, with colonies of
 * glowing lichen that thicken in the deep middle and change colour from one end to the other. Shared by the interior's walls and the
 * lower part of each entrance, so the two match exactly where they meet at a gate.
 */
export function interiorSurface(
  o: InteriorLook,
  look: N,
): { albedo: N; roughness: N; emissive: N; normal: N } {
  const p: N = positionWorld.sub(o.originRel);
  // Fine grit only close up (it would alias further away).
  const near = float(1).sub(smoothstep(5, 16, length(positionView)));
  const n1 = mx_noise_float(p.mul(0.21));
  const n2 = mx_noise_float(p.mul(1.1));
  const n3 = mx_noise_float(p.mul(3.7));
  const n4 = mx_noise_float(p.mul(11)).mul(near);
  // Layers, bent by the big blotches and the lumps: bands of lighter and darker stone.
  const layer = sin(
    dot(p, vec3(...o.strata))
      .mul((2 * Math.PI) / LAYER)
      .add(n1.mul(2.2))
      .add(n2.mul(0.7)),
  );
  // Veins of calcite: a few long, thin, pale lines through the rock, only in places.
  const nv = mx_noise_float(p.mul(0.33).add(n1.mul(0.6)));
  const vein = float(1)
    .sub(smoothstep(0.002, 0.012, abs(nv)))
    .mul(smoothstep(0.1, 0.5, n1));
  // Wet rock is smoother (and darker, and glossy).
  const wet = look.w;
  const h = n2
    .mul(0.06)
    .add(n3.mul(0.012))
    .add(n4.mul(0.0025))
    .add(layer.mul(0.012))
    .mul(float(1).sub(wet.mul(0.4)));

  // Grey limestone, from dark to pale, a little warmer or cooler in patches; brown grime in
  // the hollows between the lumps.
  const tone = smoothstep(-0.6, 0.6, n1.mul(0.8).add(n2.mul(0.35)));
  const warm = smoothstep(-0.4, 0.5, mx_noise_float(p.mul(0.07)));
  const base: N = mix(vec3(0.15, 0.148, 0.145), vec3(0.47, 0.46, 0.44), tone).mul(
    mix(vec3(0.94, 0.98, 1.05), vec3(1.07, 1.0, 0.9), warm),
  );
  const grain = n3.mul(0.07).add(n4.mul(0.06)).add(layer.mul(0.1)).add(1);
  const hollow = smoothstep(-0.95, 0.15, n2);
  const grime = mix(vec3(0.74, 0.66, 0.57), vec3(1), hollow);
  const stone: N = mix(base.mul(grain).mul(grime), vec3(0.62, 0.6, 0.56), vein.mul(0.5));
  const albedo: N = stone.mul(look.xyz).mul(float(1).sub(wet.mul(0.35)));
  const roughness = mix(float(0.86), float(0.4), wet);

  // 0 at gate A … 1 at gate B.
  const dA = length(p.sub(vec3(...o.gateA)));
  const dB = length(p.sub(vec3(...o.gateB)));
  const along = dA.div(max(dA.add(dB), 1e-3));
  const glow = mix(
    vec3(o.glowA.r, o.glowA.g, o.glowA.b),
    vec3(o.glowB.r, o.glowB.g, o.glowB.b),
    smoothstep(0.35, 0.65, along),
  );
  // Glowing lichen, in colonies: patches a few metres across, thicker in the deep middle and
  // in wet places, of small bright spots that keep to the hollows.
  const colony = smoothstep(0.1, 0.45, mx_noise_float(p.mul(0.16).add(7.3)));
  const density = smoothstep(0.05, 0.4, along.mul(float(1).sub(along)).mul(4))
    .mul(0.5)
    .add(wet.mul(0.3))
    .mul(colony);
  const spotN = mx_noise_float(p.mul(5.2)).add(n3.mul(0.2)).sub(n2.mul(0.12));
  const edge = float(0.8).sub(density.mul(0.28));
  const spots = smoothstep(edge, edge.add(0.04), spotN);
  // Glow: the spots and a little of their light on the rock around them, and a faint cool
  // fill (the lantern is warm), so the dark is never pitch black.
  // The fill comes off the open space in front of the wall, so the bumps shade it too.
  const normal = bumpNormal(h);
  const facing = dot(normal, normalViewGeometry).clamp(0, 1);
  const fill = mix(vec3(0.3, 0.36, 0.45), glow, 0.3).mul(facing.mul(facing).mul(0.3));
  const emissive = glow
    .mul(spots.mul(0.8))
    .add(albedo.mul(glow).mul(density.mul(0.16)))
    .add(albedo.mul(fill));
  return { albedo, roughness, emissive, normal };
}

/** A constant look (rgb tint, wetness) as a node, e.g. a passage's for an entrance. */
export function lookNode(look: readonly [number, number, number, number]): N {
  return vec4(...look);
}

/**
 * Rock for a cave's interior (its generated walls), tinted per space by the mesh's
 * `aLook` attribute. The player's lantern is the key light; the lichen, and a faint glow
 * off it, keep the dark from ever being pitch black.
 */
export function createInteriorMaterial(
  o: InteriorLook & { lights: THREE.LightsNode },
): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.86, metalness: 0 });
  mat.lightsNode = o.lights;
  const s = interiorSurface(o, attribute('aLook', 'vec4'));
  mat.colorNode = s.albedo;
  mat.roughnessNode = s.roughness;
  mat.emissiveNode = s.emissive;
  mat.normalNode = s.normal;
  return mat;
}
